export const workspaceArticles = [
  {
    slug: 'device-notifications',
    title: 'Device notifications',
    summary: 'Set up Device alerts, keep the notification list useful, and know where to look when a deployment needs attention.',
    category: 'workspace',
    sources: [{ file: 'notifications.pdf' }],
    sections: [
      {
        id: 'configure',
        title: 'Configure notifications',
        paragraphs: [
          'Notifications can bring a Device problem to your attention before you happen to notice it in analytics. Open notification settings from the bell menu, choose the alert types and delivery channels available to your account, then save and apply the configuration.',
          'Email delivery can include additional recipients who also need to respond to Device alerts. Keep that list focused on people who can act on the notification so operational messages remain useful.'
        ],
        bullets: [
          'Email sends configured alerts to the selected recipients.',
          'In App displays alerts in the Dashboard notification views.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/notifications-delivery-settings-email-and-in-app.jpg',
            frame: { width: 2126, height: 1008, sourceWidth: 2362 },
            alt: 'Notification settings with Email and In App channels',
            caption: 'Choose the delivery channels for each event type.'
          }
        ],
        steps: [
          'Open notification settings from the bell menu.',
          'Choose the event types and enable Email, In App, or both.',
          'Add any email recipients needed for the selected alerts, then save and apply the settings.'
        ]
      },
      {
        id: 'review',
        title: 'Review notifications',
        paragraphs: [
          'The bell menu keeps recent notifications close at hand. Select View All when you need to search the complete history, separate Inbox items from Archived items, or return to an alert after investigating it.'
        ],
        bullets: [
          'Filter notifications by their available types or show only urgent notifications.',
          'Archive notifications that no longer require attention.',
          'Restore an archived notification to the Inbox when it was archived by mistake.',
          'Delete notifications that are no longer needed.'
        ],
        steps: [
          'Open the bell menu and select View All.',
          'Use the search field and event-type filter to narrow the list.',
          'Review Inbox or Archived and archive resolved notifications when appropriate.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/notifications-inbox-search-and-archive.jpg',
            frame: { width: 2104, height: 1008, sourceWidth: 2362 },
            alt: 'Notifications page with Inbox, Archived, and search controls',
            caption: 'A new workspace may have an empty Inbox; received alerts appear here.'
          },
          {
            src: 'assets/documentation/examples/notifications-filter-by-event-type.jpg',
            frame: { width: 2104, height: 1008, sourceWidth: 2362 },
            alt: 'Notifications event-type filter dropdown',
            caption: 'Filter the history by the event types available to your account.'
          }
        ]
      },
      {
        id: 'investigate',
        title: 'Investigate a Device alert',
        paragraphs: [
          'An alert tells you where to begin, but the current Device status and recent activity provide the operational context. Understanding Device Status explains what each state means and which remote actions are available.'
        ],
        paragraphLinks: [{
          label: 'Understanding Device Status',
          slug: 'understanding-device-status',
          section: 'status-meanings'
        }],
        steps: [
          'Open the notification and identify the affected Device.',
          'Open Devices and review the Device status and recent activity.',
          'Use the Device actions that are available for its current status.'
        ]
      }
    ],
    dashboardLinks: [
      { label: 'Open Notifications', to: '/notifications' },
      { label: 'Open Devices', to: '/devices' }
    ],
    related: ['workspace-management', 'device-status-api'],
    legacy: false
  },
  {
    slug: 'workspace-management',
    title: 'Workspace management',
    summary: 'Learn how the Dashboard’s management pages connect Devices and settings with the locations, media, and integrations your team recognizes.',
    category: 'workspace',
    sources: [{ file: 'management.pdf' }],
    sections: [
      {
        id: 'find-devices',
        title: 'Find Devices',
        paragraphs: [
          'The Devices page is the operational inventory for the hardware available to your account. Search by a recognizable name or Device ID, filter by status or solution, and combine those filters when you need to narrow a large deployment. For an explanation of how Devices relate to physical locations and media, see Understanding Sites, Assets, and Devices.'
        ],
        paragraphLinks: [{
          label: 'Understanding Sites, Assets, and Devices',
          slug: 'sites-assets-and-devices',
          section: 'from-workspace-to-measurement'
        }],
        tables: [
          {
            caption: 'Device-list information',
            headers: ['Field', 'Purpose'],
            rows: [
              ['Name and Device ID', 'Identify and search for a Device.'],
              ['Solution', 'Shows the analytics application assigned to the Device.'],
              ['Status', 'Shows whether the Device is available for remote actions.'],
              ['Last detection', 'Shows the most recent reported detection time.'],
              ['Application version', 'Shows the reported application release.']
            ]
          }
        ],
        figures: [
          {
            src: 'assets/documentation/examples/management-devices-status-and-solution-filters.jpg',
            frame: { width: 2608, height: 1246, sourceWidth: 2917 },
            alt: 'Device directory with status and solution filter menus',
            caption: 'Combine status and solution filters when searching a deployment.'
          }
        ],
        steps: [
          'Open Management → Devices.',
          'Search by Device name or ID, or use the status and solution filters.',
          'Open the matching Device to review its settings.'
        ]
      },
      {
        id: 'device-actions',
        title: 'Run Device actions',
        paragraphs: [
          'Remote actions work only while a Device is communicating with the platform. The Dashboard enables an action when the current status supports it, which helps prevent commands that cannot be completed in that state.',
          'Restart, start, and stop can also be sent to several selected Devices. Give the command time to finish and check the resulting status before sending another action. Understanding Device Status covers the meaning of each state and the checks to make when it does not change as expected.'
        ],
        paragraphLinks: [{
          label: 'Understanding Device Status',
          slug: 'understanding-device-status',
          section: 'available-actions'
        }],
        tables: [
          {
            caption: 'Action availability',
            headers: ['Action', 'Available status'],
            rows: [
              ['Preview', 'Online'],
              ['Restart', 'Online or No Camera'],
              ['Stop', 'Online or No Camera'],
              ['Start', 'Stopped']
            ]
          }
        ]
      },
      {
        id: 'device-details',
        title: 'Review Device details',
        paragraphs: [
          'Open a Device when you need to move beyond list-level status. Its detail view brings together identifying information, general camera settings, render options, and the settings supplied by its solution. Treat every provisioning token shown in Device or Site Group information as a secret.'
        ],
        tables: [
          {
            caption: 'General settings',
            headers: ['Group', 'Available settings'],
            rows: [
              ['Camera configuration', 'Resolution, forced resolution, aspect ratio, rotation, and video source or path.'],
              ['Render options', 'Preview blur, blur amount, drawings, preview quality, and frame-rate limit.'],
              ['Advanced settings', 'Settings supplied by the selected solution, such as display, trigger, speed, statistics, or model controls.']
            ]
          }
        ],
        steps: [
          'Open a Device from the directory and expand General.',
          'Review the camera configuration and preview render options.',
          'Expand the solution settings to review detection and model options. Apply changes only after checking the installation requirements.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/devices-general-camera-and-preview-settings.jpg',
            frame: { width: 2626, height: 1246, sourceWidth: 2917 },
            alt: 'Device General settings with camera and preview configuration',
            caption: 'General contains camera and preview settings. This example Device is offline; it does not show a live preview.'
          },
          {
            src: 'assets/documentation/examples/devices-audience-detection-and-model-settings.jpg',
            frame: { width: 2626, height: 1246, sourceWidth: 2917 },
            alt: 'Audience Device solution settings with detection and model options',
            caption: 'Solution settings vary with the application running on the Device.'
          }
        ]
      },
      {
        id: 'zones',
        title: 'Manage regions and zones',
        paragraphs: [
          'Traffic Devices can use regions of interest and crossing zones to connect detections with a meaningful part of the scene. Request a current preview before positioning them, keep each active region inside the image, and place each zone across the path that objects cross. Vehicle camera positioning and zones provides field guidance and validation steps.'
        ],
        paragraphLinks: [{
          label: 'Vehicle camera positioning and zones',
          slug: 'vehicle-camera-positioning',
          section: 'regions-and-zones'
        }],
        bullets: [
          'The standard Dashboard editor supports up to two or three regions and zones, depending on the Traffic Device configuration.',
          'Some administrative views can allow different limits; follow the controls presented for the account.'
        ]
      },
      {
        id: 'organize',
        title: 'Organize the workspace',
        paragraphs: [
          'Site Groups define the access boundary for a workspace, while Sites connect Dashboard data with physical locations. Depending on the capabilities enabled for your account, Reports, Campaigns, Portals, Point of Sale, and Integrations add ways to distribute results or connect outside business context. Profile and Site Groups covers the settings and access controls at the top of that structure.'
        ],
        paragraphLinks: [{
          label: 'Profile and Site Groups',
          slug: 'profile-and-site-groups',
          section: 'site-group-details'
        }]
      },
      {
        id: 'create-site-settings',
        title: 'Create a Site: name and type',
        paragraphs: [
          'Select the Site Group that should own the location before starting. A clear Site name and the appropriate Site Type help users recognize it and define its coverage.'
        ],
        steps: [
          'Open Management → Sites and start creating a Site.',
          'In Settings, enter Site Name, choose Site Type, and add a description if useful.',
          'Select Next.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/sites-create-step-01-settings.jpg',
            frame: { width: 2601, height: 1246, sourceWidth: 2917 },
            alt: 'Create Site Settings step with name, type, and description',
            caption: 'Step 1: describe the location. This is an unsaved example.'
          }
        ]
      },
      {
        id: 'create-site-solution',
        title: 'Create a Site: solution',
        steps: [
          'In Solution, select the measurements supported at this location.',
          'Select Next to configure its location.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/sites-create-step-02-solution.jpg',
            frame: { width: 2601, height: 1246, sourceWidth: 2917 },
            alt: 'Create Site Solution step with supported measurement options',
            caption: 'Step 2: choose the data sources used at this Site.'
          }
        ]
      },
      {
        id: 'create-site-location',
        title: 'Create a Site: location and coverage',
        steps: [
          'In Location, search for the address or enter the coordinates.',
          'Check the marker and coverage area on the map. The selected Site Type supplies the radius for radius-based coverage.',
          'Select Next to review Assets.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/sites-create-step-03-location-and-coverage-radius.jpg',
            frame: { width: 2601, height: 1246, sourceWidth: 2917 },
            alt: 'Create Site Location step showing coordinates and map coverage radius',
            caption: 'Step 3: verify the location and coverage before saving the Site.'
          }
        ]
      },
      {
        id: 'site-assets',
        title: 'Add Assets and attach Devices',
        paragraphs: [
          'For a Mobile-only Site, the Assets step can remain empty; select Skip & Submit to finish creation. For a camera-based deployment, add the Assets that represent the placements and associate their Devices.',
          'For an existing Site, open its Assets step to review the list or edit an Asset. One Asset can use multiple Devices, and multiple Assets can share a Device when they measure the same scene.'
        ],
        steps: [
          'Open the Site and its Assets step.',
          'Add or edit an Asset and select the Devices that measure that placement.',
          'Save the Asset, then review the Site’s Asset list and Device associations.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/sites-mission-assets-and-device-counts.jpg',
            frame: { width: 2626, height: 1245, sourceWidth: 2917 },
            alt: 'Mission Site Assets list with multiple placements and Device counts',
            caption: 'Review each Asset and its attached Devices after saving.'
          }
        ]
      },
      {
        id: 'bulk-sites',
        title: 'Create several Sites from a file',
        steps: [
          'Open the bulk-create option on the Sites page.',
          'Download the template supplied by the dialog and fill it with your location records.',
          'Choose the completed file, review any validation messages, and submit when the records are valid.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/sites-bulk-create-file-upload.jpg',
            frame: { width: 2626, height: 1245, sourceWidth: 2917 },
            alt: 'Bulk Site creation dialog with template download and file selection',
            caption: 'Use the current template when preparing a bulk Site import.'
          }
        ]
      }
    ],
    dashboardLinks: [
      { label: 'Open Sites', to: '/sites' },
      { label: 'Open Site Groups', to: '/sitegroups' },
      { label: 'Open Devices', to: '/devices' },
      { label: 'Open Campaigns', to: '/campaigns' },
      { label: 'Open Portals', to: '/portals' },
      { label: 'Open Point of Sale', to: '/pos' },
      { label: 'Open Integrations', to: '/integrations' }
    ],
    related: ['profile-and-site-groups', 'device-notifications', 'campaigns-and-brand-portals', 'web-attribution', 'retail-media-and-sales'],
    legacy: false
  },
  {
    slug: 'campaigns-and-brand-portals',
    title: 'Campaigns and Brand Portals',
    summary: 'Set up campaign dates and Sites, connect a Brand Portal, and invite the brand team to review its results.',
    category: 'workspace',
    sources: [],
    sections: [
      {
        id: 'campaign-scope',
        title: 'Start with the campaign scope',
        paragraphs: [
          'A campaign defines the period and Sites for an activation. A Brand Portal gives the brand team access to its associated campaigns. These setup steps are performed by an authorized workspace user; the brand team then signs in to its own portal view.',
          'The examples use the fictional Cedarline workspace. Names, dates, and numbers illustrate the workflow; use your own campaign schedule and location assignments.'
        ],
        steps: [
          'Open Brand → Campaigns and review the existing campaign list.',
          'Confirm the planned dates and participating Sites before creating a campaign.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/brand-campaigns-directory-and-schedule.jpg',
            frame: { width: 2113, height: 1008, sourceWidth: 2362 },
            alt: 'Campaign directory showing campaign names and date ranges',
            caption: 'The campaign list shows the schedule for each activation.'
          }
        ]
      },
      {
        id: 'campaign-settings',
        title: 'Step 1: name and dates',
        steps: [
          'Start creating a campaign.',
          'Enter Campaign Name and Start Date.',
          'For a fixed campaign, enter End Date. Use Ongoing Campaign only when no fixed end date is needed.',
          'Select Next.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/brand-create-campaign-step-01-name-and-dates.jpg',
            frame: { width: 2102, height: 1008, sourceWidth: 2362 },
            alt: 'Campaign creation Settings step with name and start and end dates',
            caption: 'This draft example covers one month. Set the dates for your own activation.'
          }
        ]
      },
      {
        id: 'campaign-sites',
        title: 'Step 2: participating Sites',
        steps: [
          'In Sites, select the locations participating in the campaign.',
          'Review the selected Site tags and remove locations outside the activation.',
          'Select Next.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/brand-create-campaign-step-02-select-sites.jpg',
            frame: { width: 2102, height: 1008, sourceWidth: 2362 },
            alt: 'Campaign creation Sites step with a selected location',
            caption: 'Choose participating locations before connecting the Brand Portal.'
          }
        ]
      },
      {
        id: 'campaign-portal',
        title: 'Step 3: connect the Brand Portal',
        steps: [
          'In Brand Portal, select an existing portal or choose the option to create a new one.',
          'For an existing portal, select it from the list. For a new portal, enter Portal Name.',
          'Review the campaign setup and select Submit.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/brand-create-campaign-step-03-select-brand-portal.jpg',
            frame: { width: 2102, height: 1008, sourceWidth: 2362 },
            alt: 'Campaign creation Brand Portal step with an existing portal selected',
            caption: 'Connect the campaign to the portal where the brand team will review it.'
          }
        ]
      },
      {
        id: 'review-campaign',
        title: 'Review the saved campaign',
        steps: [
          'Open the campaign from the directory.',
          'Confirm its name and dates in the details.',
          'Review the assigned Sites and update the selection when the activation changes.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/brand-campaign-assigned-sites.jpg',
            frame: { width: 2127, height: 1008, sourceWidth: 2362 },
            alt: 'Saved campaign Site assignments for Mission, Grand Avenue, and Fourth Street',
            caption: 'The saved example campaign includes three participating Sites.'
          }
        ]
      },
      {
        id: 'portal-settings',
        title: 'Review portal settings and capabilities',
        steps: [
          'Open Brand → Portals and select the portal.',
          'Review its name, description, and associated campaigns.',
          'Review the available features. Point Of Sale and Web Analytics provide their respective attribution workflows when enabled.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/brand-portal-name-and-description-settings.jpg',
            frame: { width: 2127, height: 1008, sourceWidth: 2362 },
            alt: 'Brand Portal settings with name and description',
            caption: 'Use a name the brand team will recognize.'
          },
          {
            src: 'assets/documentation/examples/brand-portal-attribution-feature-availability.jpg',
            frame: { width: 2127, height: 1008, sourceWidth: 2362 },
            alt: 'Brand Portal features with Point Of Sale and Web Analytics enabled',
            caption: 'Feature availability controls the workflows offered to the portal’s users.'
          }
        ]
      },
      {
        id: 'invite-brand-users',
        title: 'Invite the brand team',
        steps: [
          'Open the portal’s Users section and choose the invitation action.',
          'Enter the recipient email address and select Add.',
          'Review the invitation list, then select Submit.',
          'Ask the recipient to follow the invitation and sign in with their own account.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/brand-portal-invite-user-form.jpg',
            frame: { width: 2127, height: 1008, sourceWidth: 2362 },
            alt: 'Brand Portal invitation dialog with email entry and Add action',
            caption: 'Invite brand users from the portal that contains their campaigns.'
          }
        ]
      },
      {
        id: 'brand-user-view',
        title: 'Brand users: open campaign Charts',
        paragraphs: [
          'After accepting the invitation, sign in with your own Brand account. Your portal determines which campaigns, templates, and exports are available. These examples show Nina’s Cedarline Campaign Insights portal and its July 1–31 Autumn Launch campaign.'
        ],
        steps: [
          'Open Analytics → Charts.',
          'Open the Dashboard selector beside the Analytics title.',
          'Choose Audience, Mobile, Traffic, or Retail Media from the available templates and wait for the widgets to load.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/brand-user-nina/choose-audience-mobile-traffic-or-retail-media.jpg',
            alt: 'Nina’s Dashboard selector offering Audience, Mobile, Traffic, and Retail Media',
            caption: 'Choose a template from your own Brand session. Available templates vary by portal.'
          }
        ]
      },
      {
        id: 'brand-campaign-filters',
        title: 'Confirm the campaign, Sites, and period',
        steps: [
          'Expand the filters using the arrow at the left edge of the charts.',
          'Under Campaigns, select the campaigns to review. If you change the selection, select Update Analytics.',
          'Under Sites, select the participating locations to include. Select Apply after changing the selection.',
          'Check the date range, time grouping, and timezone before comparing results. Your portal may provide a fixed campaign period.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/brand-user-nina/filters-campaign-and-participating-sites.jpg',
            alt: 'Brand campaign filters with Autumn Launch and its three participating Sites selected',
            caption: 'Nina’s example includes Mission District, Grand Avenue, and Fourth Street for July 1–31.'
          }
        ]
      },
      {
        id: 'brand-audience-results',
        title: 'Review Audience results',
        steps: [
          'Select Audience and review Impressions, Confirmed Views, and Engagement Rate.',
          'Compare the time series and demographic distributions for the same campaign selection.',
          'Scroll to attention, age and gender breakdowns, emotions, and zone results. Use each widget’s information control for its metric definition.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/brand-user-nina/audience-campaign-kpis-and-demographics.jpg',
            alt: 'Populated Brand Audience dashboard with campaign KPIs, direction, gender, and age charts',
            caption: 'Nina’s July campaign shows 189.82K Impressions and 44.81K Confirmed Views.'
          },
          {
            src: 'assets/documentation/examples/brand-user-nina/audience-attention-and-demographic-breakdowns.jpg',
            alt: 'Brand Audience attention time and age and gender breakdowns',
            caption: 'Scroll below the overview to compare attention and demographic patterns.'
          },
          {
            src: 'assets/documentation/examples/brand-user-nina/audience-emotions-and-zones.jpg',
            alt: 'Brand Audience emotion distributions and detections by zone',
            caption: 'Zone results show where detections occurred within the campaign’s participating locations.'
          }
        ]
      },
      {
        id: 'brand-traffic-results',
        title: 'Review Traffic results',
        steps: [
          'Select Traffic while keeping the campaign and Sites consistent.',
          'Review vehicle totals, peak count, estimated impressions, and vehicle types.',
          'Scroll to speed, weekday, and hourly charts to inspect traffic patterns.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/brand-user-nina/traffic-counts-vehicle-types-and-estimated-impressions.jpg',
            alt: 'Populated Brand Traffic totals and vehicle-type breakdown',
            caption: 'Nina’s campaign shows 85.81K vehicles and a peak count of 1,721.'
          },
          {
            src: 'assets/documentation/examples/brand-user-nina/traffic-speed-and-hourly-weekday-patterns.jpg',
            alt: 'Brand Traffic vehicle speed, weekday totals, and hourly patterns',
            caption: 'Use hourly and weekday breakdowns to compare patterns within the selected period.'
          }
        ]
      },
      {
        id: 'brand-mobile-results',
        title: 'Review Mobile activity and visitor profiles',
        steps: [
          'Select Mobile and review Foot Traffic, Impressions, Frequency, and Reach.',
          'Use the metric information controls to check what each total measures. Impressions and Reach can be equal for a selection without overlapping exposure.',
          'Scroll to household income, interactions, purchase intent, age, and generation. See Working with Mobile Data for the visitor-profile walkthrough.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/brand-user-nina/mobile-impressions-reach-frequency-and-interests.jpg',
            alt: 'Brand Mobile campaign activity totals and visitor interests',
            caption: 'Nina’s July selection shows 18,594 Foot Traffic and 17,110 Impressions and Reach.'
          }
        ]
      },
      {
        id: 'brand-retail-results',
        title: 'Compare audience exposure and sales',
        steps: [
          'Select Retail Media to review audience exposure, foot traffic, and visitor profiles together.',
          'Scroll to Sales Uplift - Product Sales vs Screen Impressions.',
          'Choose the product or SKU and compare its sales with screen impressions for the selected period. See Retail Media and Point of Sale for the detailed workflow.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/brand-user-nina/retail-media-audience-and-foot-traffic.jpg',
            alt: 'Populated Brand Retail Media audience and foot-traffic overview',
            caption: 'Retail Media combines campaign audience and Mobile activity in one template.'
          }
        ]
      },
      {
        id: 'brand-share-results',
        title: 'Review website outcomes and share a report',
        steps: [
          'When Web Analytics is enabled, open Attribution to review exposed devices, website visits, and conversion. Follow the Web attribution guide for property setup and result interpretation.',
          'Return to Charts and check the template, campaign, Sites, and period you want to share.',
          'Select Generate Report → Quick PDF. In the browser print dialog, choose Save as PDF, review the preview, and save the report.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/brand-user-nina/reports-quick-pdf-menu.jpg',
            alt: 'Nina’s populated Audience dashboard with the Quick PDF report menu open',
            caption: 'Nina’s portal offers Quick PDF for sharing the Charts page.'
          }
        ]
      }
    ],
    dashboardLinks: [
      { label: 'Open Campaigns', to: '/campaigns' },
      { label: 'Open Portals', to: '/portals' }
    ],
    related: ['workspace-management', 'mobile-data-in-the-dashboard', 'web-attribution', 'retail-media-and-sales', 'exports-and-reports'],
    legacy: false
  },
  {
    slug: 'web-attribution',
    title: 'Web Embed and Web attribution',
    summary: 'Connect campaign exposure with website visits using a Web property, an attribution window, and the supplied tracking code.',
    category: 'workspace',
    sources: [],
    sections: [
      {
        id: 'web-properties',
        title: 'Review Web properties',
        paragraphs: [
          'A Web property connects a destination URL with one or more campaigns and an attribution window. In the Dashboard, these properties are managed under Web Embed. This guide uses Nina’s Brand-user view; Web Analytics must be available in the portal.'
        ],
        steps: [
          'Open Management → Web Embed.',
          'Review the property name, destination, attribution window, and status.',
          'Open an existing property to review its details, or start creating a new Web Embed.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/brand-user-nina/web-properties-destination-attribution-window-and-status.jpg',
            frame: { width: 2113, height: 1008, sourceWidth: 2362 },
            alt: 'Brand-user Web Embed directory showing destination URL, attribution window, and Active status',
            caption: 'The directory shows the configured destination and connection status for each Web property.'
          }
        ]
      },
      {
        id: 'web-details',
        title: 'Step 1: name and destination',
        paragraphs: [
          'Use a real destination that your campaign visitors can reach. The demo.example URL in this screenshot is an example only.'
        ],
        steps: [
          'In Web Embed details, enter Name and Destination URL.',
          'Check the URL, then select Next.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/brand-user-nina/web-property-create-step-01-name-and-destination.jpg',
            frame: { width: 2102, height: 1008, sourceWidth: 2362 },
            alt: 'Web Embed creation details step with name and destination URL',
            caption: 'Step 1: identify the property and the page visitors should reach.'
          }
        ]
      },
      {
        id: 'web-targeting',
        title: 'Step 2: campaigns and attribution window',
        paragraphs: [
          'The attribution window defines how long after an exposure a supported visit can be associated with it. Choose a window that matches your measurement plan before comparing results.'
        ],
        steps: [
          'In Attribution, select the campaigns associated with the destination.',
          'Choose Attribution Window and review the selected campaign tags.',
          'Select Next.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/brand-user-nina/web-property-create-step-02-campaign-and-attribution-window.jpg',
            frame: { width: 2102, height: 1008, sourceWidth: 2362 },
            alt: 'Web Embed Attribution step with a campaign and 30-day window selected',
            caption: 'Step 2: this example connects Autumn Launch with a 30-day attribution window.'
          }
        ]
      },
      {
        id: 'activate-and-track',
        title: 'Step 3: activate and connect tracking',
        paragraphs: [
          'Activate saves the Web Embed and retrieves the tracking information. The screenshot shows the review stage before activation. A saved property remains Pending Data until the platform reports receiving tracking data.'
        ],
        steps: [
          'Review the setup at Activation and QR code, then select Activate when it is correct.',
          'After activation succeeds, review the returned destination and QR tracking URL, then select Next.',
          'In Tracking code, copy the supplied snippet and have your website team install it on the intended pages.',
          'Verify that the connection becomes active after tracking data is received, then select Done.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/brand-user-nina/web-property-create-step-03-activation-review.jpg',
            frame: { width: 2102, height: 1008, sourceWidth: 2362 },
            alt: 'Web Embed wizard at Ready to activate, before the property is saved',
            caption: 'Step 3: activation creates the property. This example stops before Activate.'
          }
        ]
      },
      {
        id: 'attribution-results',
        title: 'Read Web attribution results',
        paragraphs: [
          'An active connection and campaign exposure data are needed before you can review attribution. Keep the campaign, date range, and attribution window in mind when reading the displayed metrics. Use the metric help controls to confirm their definitions.'
        ],
        steps: [
          'Open Attribution and select the relevant campaign and period.',
          'Review exposed devices, website visits, and the displayed conversion rate.',
          'Compare the exposure and visit trends over time.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/brand-user-nina/web-attribution-exposed-devices-visits-and-conversion.jpg',
            frame: { width: 2127, height: 1008, sourceWidth: 2362 },
            alt: 'Brand-user Web attribution KPIs and exposure and website-visit trends',
            caption: 'Nina’s July example shows populated exposure, visit, and conversion metrics.'
          }
        ]
      },
      {
        id: 'landing-pages-and-sites',
        title: 'Investigate landing pages and locations',
        steps: [
          'Scroll below the Attribution KPIs.',
          'Compare landing-page results and the selected Site locations.',
          'Use daily visits to identify when website activity changed. Keep the same campaign and period while comparing these widgets.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/brand-user-nina/web-attribution-landing-pages-site-map-and-daily-visits.jpg',
            frame: { width: 2128, height: 1008, sourceWidth: 2362 },
            alt: 'Web attribution landing-page results, Site map, and daily website visits',
            caption: 'Landing pages, geography, and daily trends add context to the headline attribution metrics.'
          }
        ]
      }
    ],
    dashboardLinks: [
      { label: 'Open Charts', to: '/charts' }
    ],
    related: ['campaigns-and-brand-portals', 'mobile-data-in-the-dashboard', 'exports-and-reports'],
    legacy: false
  },
  {
    slug: 'retail-media-and-sales',
    title: 'Retail Media and Point of Sale',
    summary: 'Upload sales records for a Brand Portal and compare the available sales results with screen exposure.',
    category: 'workspace',
    sources: [],
    sections: [
      {
        id: 'sales-scope',
        title: 'Prepare the sales scope',
        paragraphs: [
          'Point of Sale records add sales context to campaign analytics. An authorized workspace user uploads the file for the selected Brand Portal; its users review the available results in Retail Media.',
          'Confirm that the portal has Point Of Sale enabled and that the sales records refer to the participating Sites and campaign period.'
        ],
        steps: [
          'Open Brand → Point Of Sale.',
          'Choose the destination portal and review its upload history.',
          'Select Upload a new file.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/pos-upload-history-and-portal-selection.jpg',
            frame: { width: 2114, height: 1008, sourceWidth: 2362 },
            alt: 'Point Of Sale page with portal selector and empty upload history',
            caption: 'Choose the portal before uploading. This example has no files in its upload history.'
          }
        ]
      },
      {
        id: 'upload-sales',
        title: 'Prepare and upload the CSV',
        paragraphs: [
          'Download Template supplies the current CSV format and an example row. Replace the example with your own records and preserve the template headers. Check Site identifiers, product and SKU labels, quantities, values, and timestamps before uploading.'
        ],
        steps: [
          'In the upload dialog, select Download Template.',
          'Populate the comma-separated CSV using the downloaded headers, including site_id and qty.',
          'Select Choose File and resolve any validation message.',
          'Select Upload File. Wait for the processing notification before using the results in a report.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/pos-upload-csv-template-and-required-columns.jpg',
            frame: { width: 2127, height: 1008, sourceWidth: 2362 },
            alt: 'Point of Sale upload dialog with template and CSV file controls',
            caption: 'Use the downloaded template as the field reference when preparing a sales file.'
          }
        ]
      },
      {
        id: 'sales-results',
        title: 'Compare sales and exposure',
        paragraphs: [
          'The Retail Media template combines the data available to the selected campaign. The following example shows Nina’s sales comparison widget. Its title and metric help describe the calculation; a visual association between sales and impressions alone does not establish causation.'
        ],
        steps: [
          'As a Brand user, open Analytics → Charts and select Retail Media.',
          'Confirm the campaign and date range, then find Sales Uplift vs Screen Impressions.',
          'Review the SKU selection and compare the sales and exposure series for the same period.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/brand-user-nina/retail-media-sales-uplift-vs-screen-impressions.jpg',
            alt: 'Brand-user Retail Media sales uplift and screen impression chart with SKU selector',
            caption: 'Select the product or SKU before interpreting the sales comparison.'
          }
        ]
      }
    ],
    dashboardLinks: [
      { label: 'Open Point Of Sale', to: '/pos' },
      { label: 'Open Portals', to: '/portals' }
    ],
    related: ['campaigns-and-brand-portals', 'audience-metrics', 'exports-and-reports'],
    legacy: false
  }
];
